import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, Phone, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";

interface ScheduleCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ScheduleCallModal = ({ isOpen, onClose }: ScheduleCallModalProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    date: "",
    description: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.phone) {
      toast({
        title: "❌ Missing Information",
        description: "Please fill in all required fields (Name and Phone Number).",
        variant: "destructive"
      });
      return;
    }
    
    // Create WhatsApp message
    const whatsappMessage = `📞 *Call Schedule Request* 📞

👤 *Contact Information*
• Name: ${formData.name}
• Phone: ${formData.phone}
• Preferred Date: ${formData.date || 'Not specified'}

📝 *Description*
${formData.description || 'No additional details provided.'}

I would like to schedule a call at the proposed time. Please confirm if this works for you.`;
    
    try {
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/919324471736?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      
      // Reset form
      setFormData({
        name: "",
        phone: "",
        date: "",
        description: ""
      });
      
      // Show success message
      toast({
        title: "📞 WhatsApp Opened!",
        description: "Please confirm your call details with our team on WhatsApp.",
        duration: 5000
      });
      
      // Close the modal
      onClose();
      
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      toast({
        title: "❌ Error",
        description: "Could not open WhatsApp. Please try again or contact us directly.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white w-[95vw] max-w-[420px] sm:max-w-md mx-auto my-4 sm:my-0 rounded-lg overflow-y-auto max-h-[90vh] sm:max-h-[80vh]">
        <DialogHeader className="relative pb-2">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center text-lg sm:text-xl">
              <Calendar className="h-5 w-5 mr-2 text-blue-400 flex-shrink-0" />
              <span>Schedule a Call</span>
            </DialogTitle>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white focus:outline-none p-1 -mr-2"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 px-0">
          <div>
            <Label className="text-white text-sm sm:text-base">Full Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="bg-slate-800 border-slate-600 text-white text-base h-11"
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <Label className="text-white text-sm sm:text-base">Phone Number *</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="bg-slate-800 border-slate-600 text-white text-base h-11"
              placeholder="Enter your phone number"
              required
            />
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div>
              <Label className="text-white text-sm sm:text-base">Preferred Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="bg-slate-800 border-slate-600 text-white text-base h-11"
                required
              />
            </div>

            <div>
              <Label className="text-white text-sm sm:text-base">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-800 border-slate-600 text-white text-base min-h-[100px]"
                placeholder="Tell us about your loan requirement..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-1">
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-base h-11"
            >
              <MessageSquare className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Schedule via WhatsApp</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-green-500 text-green-400 hover:bg-green-500/10"
              onClick={() => window.open('tel:+919324471736')}
            >
              <Phone className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-sm text-gray-400 text-center">
            Our team will contact you within 2 hours to confirm the schedule
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
